import React, { useState, useEffect, useMemo } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { 
  FaEye, FaTrashAlt, FaSearch, FaTimes, FaUser, 
  FaShippingFast, FaCreditCard, FaBoxOpen, FaCalendarAlt, FaTimesCircle 
} from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';

const CancelledOrderList: React.FC = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  
  // States
  const [searchIdOrName, setSearchIdOrName] = useState<string>(''); 
  const [filterPayment, setFilterPayment] = useState<string>('All'); 
  const [filterDate, setFilterDate] = useState<string>(''); 
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);

  // Fetch Logic
  const fetchCancelledOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get('https://ayushvatika.com/api/orders/all', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const cancelledOnly = res.data.filter((order: any) => order.status === 'CANCELLED');
      setOrders(cancelledOnly);
    } catch (err: any) {
      toast.error(err.response?.data || "Cancelled orders load nahi ho paye!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCancelledOrders();
  }, []);

  // Filter Logic
  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      const customerFullName = `${order.user.firstName} ${order.user.lastName}`.toLowerCase();
      const matchesSearch = order.id.toLowerCase().includes(searchIdOrName.toLowerCase()) || 
                            customerFullName.includes(searchIdOrName.toLowerCase());
      
      const matchesPayment = filterPayment === 'All' || 
                             order.paymentMode === filterPayment || 
                             order.paymentStatus === filterPayment;

      const orderDateOnly = new Date(order.orderDate).toISOString().split('T')[0];
      const matchesDate = !filterDate || orderDateOnly === filterDate;

      return matchesSearch && matchesPayment && matchesDate;
    });
  }, [orders, searchIdOrName, filterPayment, filterDate]);

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this record forever?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`https://ayushvatika.com/api/orders/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success("Order record deleted!");
      fetchCancelledOrders();
    } catch (err: any) {
      toast.error("Delete failed!");
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors">
        <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-xl shadow-sm">
                <FaTimesCircle size={24}/>
            </div>
            <h1 className="text-3xl font-black text-gray-900 dark:text-white tracking-tight">Cancelled Orders</h1>
        </div>
        
        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8 bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="relative">
                <input
                  type="text"
                  placeholder="Search ID/Name..."
                  className="w-full p-3 pl-10 border rounded-xl text-sm focus:ring-2 focus:ring-red-500 outline-none
                  bg-gray-50 border-gray-200 text-gray-900
                  dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  value={searchIdOrName}
                  onChange={(e) => setSearchIdOrName(e.target.value)}
                />
                <FaSearch className="absolute left-3.5 top-4 text-gray-400" />
            </div>
            <select 
                className="p-3 border rounded-xl text-sm outline-none focus:ring-2 focus:ring-red-500
                bg-gray-50 border-gray-200 text-gray-900
                dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                value={filterPayment}
                onChange={(e) => setFilterPayment(e.target.value)}
            >
                <option value="All">All Payments</option>
                <option value="COD">COD</option>
                <option value="PAID">PAID</option>
                <option value="UNPAID">UNPAID</option>
            </select>
            <div className="relative">
                <input 
                    type="date"
                    className="w-full p-3 pl-10 border rounded-xl text-sm outline-none focus:ring-2 focus:ring-red-500
                    bg-gray-50 border-gray-200 text-gray-900
                    dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                />
                <FaCalendarAlt className="absolute left-3.5 top-4 text-gray-400" />
            </div>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700 uppercase text-[10px] font-black text-gray-400 dark:text-gray-300 tracking-widest">
                <tr>
                  <th className="px-6 py-4 text-left">Order ID</th>
                  <th className="px-6 py-4 text-left">Customer</th>
                  <th className="px-6 py-4 text-left hidden sm:table-cell">Cancel Date</th>
                  <th className="px-6 py-4 text-left">Amount</th>
                  <th className="px-6 py-4 text-left">Status</th>
                  <th className="px-6 py-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                {loading ? (
                  <tr><td colSpan={6} className="text-center py-20 italic text-gray-400">Loading cancelled orders...</td></tr>
                ) : filteredOrders.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-20 text-gray-400 font-bold">No cancelled orders found.</td></tr>
                ) : filteredOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-red-50/10 dark:hover:bg-red-900/10 transition-all">
                    <td className="px-6 py-4 text-xs font-mono font-bold text-red-600 dark:text-red-400 whitespace-nowrap">{order.id}</td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-black text-gray-800 dark:text-white">{order.user.firstName}</div>
                      <div className="text-[10px] text-gray-400 hidden sm:block">{order.user.email}</div>
                    </td>
                    <td className="px-6 py-4 text-xs text-gray-600 dark:text-gray-400 font-bold hidden sm:table-cell">
                      {new Date(order.orderDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm font-black text-gray-900 dark:text-white whitespace-nowrap">₹{order.totalAmount}</td>
                    <td className="px-6 py-4">
                        <span className="px-3 py-1 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-300 border border-red-100 dark:border-red-800 rounded-xl text-[10px] font-black uppercase shadow-sm">
                            {order.status}
                        </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                        <div className="flex justify-center gap-3">
                            <button onClick={() => { setSelectedOrder(order); setShowModal(true); }} className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-xl transition-all shadow-sm active:scale-90"><FaEye size={18}/></button>
                            <button onClick={() => handleDelete(order.id)} className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-xl transition-all shadow-sm active:scale-90"><FaTrashAlt size={18}/></button>
                        </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal - Dark Mode Compatible */}
      {showModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-[2.5rem] w-full max-w-4xl max-h-[95vh] overflow-y-auto shadow-2xl border-4 border-gray-50 dark:border-gray-700">
            
            <div className="sticky top-0 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm px-10 py-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center z-10">
              <div>
                <h2 className="text-3xl font-black text-gray-900 dark:text-white tracking-tighter">Cancelled Order Detail</h2>
                <p className="text-xs text-red-600 dark:text-red-400 font-mono mt-1 font-bold">ID: {selectedOrder.id}</p>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-gray-400 hover:text-red-500 transition-all">
                <FaTimes size={24} />
              </button>
            </div>

            <div className="p-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-10">
                  <div className="space-y-8">
                    <div>
                        <h3 className="text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-[0.3em] flex items-center gap-3 mb-4"><FaUser/> Customer Profile</h3>
                        <div className="bg-gray-50 dark:bg-gray-700/50 p-6 rounded-3xl border border-gray-100 dark:border-gray-600">
                            <p className="text-lg font-black text-gray-900 dark:text-white">{selectedOrder.user.firstName} {selectedOrder.user.lastName}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 font-semibold">{selectedOrder.user.email}</p>
                        </div>
                    </div>
                    <div>
                        <h3 className="text-[10px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-[0.3em] flex items-center gap-3 mb-4"><FaShippingFast/> Delivery Address</h3>
                        <div className="bg-gray-50 dark:bg-gray-700/50 p-6 rounded-3xl border border-gray-100 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300 leading-relaxed font-semibold italic">
                            {selectedOrder.shippingAddressSnapshot}
                        </div>
                    </div>
                  </div>

                  <div className="space-y-8">
                    <div>
                        <h3 className="text-[10px] font-black text-orange-600 dark:text-orange-400 uppercase tracking-[0.3em] flex items-center gap-3 mb-4"><FaCreditCard/> Order Summary</h3>
                        <div className="bg-gray-50 dark:bg-gray-700/50 p-7 rounded-3xl border border-gray-100 dark:border-gray-600 space-y-5">
                            <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-600 pb-3">
                                <span className="text-xs text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest">Status</span>
                                <span className="px-3 py-1 rounded-xl text-[10px] font-black uppercase bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-300">CANCELLED</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-xs text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest">Method</span>
                                <span className="text-sm font-black text-gray-900 dark:text-white">{selectedOrder.paymentMode || 'COD'}</span>
                            </div>
                            <div className="flex justify-between items-end border-t-2 border-dashed border-gray-200 dark:border-gray-600 pt-5">
                                <span className="text-sm font-black text-gray-600 dark:text-gray-400">Total Amount</span>
                                <span className="text-3xl font-black text-red-700 dark:text-red-400">₹{selectedOrder.totalAmount}</span>
                            </div>
                        </div>
                    </div>
                  </div>
                </div>

                <div className="mt-10 pt-8 border-t-2 border-gray-50 dark:border-gray-700">
                    <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.4em] mb-6 flex items-center gap-3"><FaBoxOpen/> Product Summary</h3>
                    <div className="grid grid-cols-1 gap-4">
                        {selectedOrder.orderItems?.map((item: any, idx: number) => (
                          <div key={idx} className="flex flex-col sm:flex-row items-center gap-5 bg-white dark:bg-gray-800 p-5 border border-gray-100 dark:border-gray-700 rounded-[1.5rem]">
                            <img src={item.imageUrl || '/Logo/default-product.png'} className="w-16 h-16 rounded-2xl object-cover bg-gray-50 border border-gray-100" alt="" />
                            <div className="flex-1 text-center sm:text-left min-w-0">
                                <p className="text-base font-black text-gray-900 dark:text-white truncate">{item.productName}</p>
                                <p className="text-xs text-gray-400 font-bold mt-1 uppercase tracking-tighter">Qty: {item.quantity} | Rate: ₹{item.price}</p>
                            </div>
                            <div className="text-xl font-black text-gray-900 dark:text-white pr-2">₹{item.quantity * item.price}</div>
                          </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="px-8 py-6 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-100 dark:border-gray-700 flex justify-end">
                <button onClick={() => setShowModal(false)} className="w-full sm:w-auto px-12 py-3 bg-gray-900 dark:bg-black text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-black dark:hover:bg-gray-900 transition-all shadow-lg">Close Detail</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default CancelledOrderList;